import { Select } from '@common/ui'
import { NextElementType } from '../data/NextElementDisplay'
import { LocalNext } from '../../domain/form/definition/LocalNext'
import { useFieldError } from '../../util/FormErrorBus'
import { useEffect, useMemo } from 'react'
import NextOption from './NextOption'

export type NextElementInputProps = {
  value?: LocalNext
  parentKey: string
  type: NextElementType
  onChange: (newValue: LocalNext | undefined) => void
  options?: readonly NextOption[]
}

const NextElementInput = ({ value, parentKey, type, onChange, options = [] }: NextElementInputProps) => {
  const defaultText = type === 'page' ? 'Next page' : 'Next item'

  const { hasError, setError, clearError } = useFieldError(parentKey)
  const currentOption = value ? options?.find(({ next }) => next.key === value.key)?.label : undefined
  const fieldHasError = !!value && !currentOption
  useEffect(() => {
    if (fieldHasError) setError('')
    else clearError()
  }, [fieldHasError])

  const pageOptions = useMemo(() => options.filter(({ next }) => next.type === 'page'), [options])
  const itemOptions = useMemo(() => options.filter(({ next }) => next.type === 'item'), [options])

  const knownKeys = new Set(options.map(({ next }) => next.key))
  const unknownValueItems = value && !knownKeys.has(value.key) ? [{ value: value.key, label: value.key }] : []

  const data =
    type === 'page'
      ? [
          { value: '', label: defaultText },
          ...pageOptions.map(({ next, label }) => ({ value: next.key, label })),
          ...unknownValueItems,
        ]
      : [
          { value: '', label: defaultText },
          ...(itemOptions.length > 0
            ? [
                {
                  group: 'Elements on this page',
                  items: itemOptions.map(({ next, label }) => ({ value: next.key, label })),
                },
              ]
            : []),
          ...(pageOptions.length > 0
            ? [{ group: 'Other pages', items: pageOptions.map(({ next, label }) => ({ value: next.key, label })) }]
            : []),
          ...unknownValueItems,
        ]

  return (
    // Fixed width: otherwise the select grows with its selected label and overflows narrow panels.
    // Long labels truncate with an ellipsis; the dropdown still shows them in full.
    <Select
      label="Go to"
      w={220}
      style={{ flexShrink: 0 }}
      error={hasError ? `Invalid ${value?.type} selected` : undefined}
      value={value?.key ?? ''}
      data={data}
      allowDeselect={false}
      onChange={(newValue) => {
        if (!newValue) {
          onChange(undefined)
        } else {
          const nextValue = options.find(({ next }) => next.key === newValue)?.next
          onChange(nextValue)
        }
      }}
    />
  )
}

export default NextElementInput
